// This file is automatically generated by webgen from the struct descriptions in /generated-json/structs.
// Files in /generated-json are created from the structs configured in the 'main' function of /cmd/struct2json/main.go.
//
// DO NOT EDIT THIS FILE except where designated below.

/* eslint-disable no-relative-import-paths/no-relative-import-paths */

// webgen:import { "defaultName": "Enums", "path": "../Enums" }
// webgen:import { "names": ["List"], "path": "../../../types/list" }
// webgen:import { "wildcardName": "CurrencyHelpers", "path": "../../../utils/currency" }
// webgen:import { "names": ["Forecast"], "path": "../Forecast/Forecast" }
// webgen:import { "typeNames": ["Units"], "path": "../../units" }

import type { DateTime, Duration } from 'luxon';
import type { PartialDeep } from 'type-fest';

import { List } from '../../../types/list';
import * as CurrencyHelpers from '../../../utils/currency';
import * as DateHelpers from '../../../utils/dateHelpers';
import type { ExcludeMethodsDeep } from '../../../utils/types';
import { NIL_UUID } from '../../../utils/uuid';
import type { Units } from '../../units';
import { Address, type AddressWireFormat, NewAddress } from '../Address/Address';
import { Currency, type CurrencyWireFormat, NewCurrency } from '../Currency/Currency';
import Enums from '../Enums';
import { Forecast, type ForecastWireFormat, NewForecast } from '../Forecast/Forecast';
import { NewPlant, Plant, type PlantWireFormat } from '../Plant/Plant';
import {
  NewProjectCompany,
  type ProjectCompany,
  type ProjectCompanyWireFormat,
} from '../ProjectCompany/ProjectCompany';
import {
  NewProjectCompetitor,
  type ProjectCompetitor,
  type ProjectCompetitorWireFormat,
} from '../ProjectCompetitor/ProjectCompetitor';
import {
  NewProjectConfig,
  type ProjectConfig,
  type ProjectConfigWireFormat,
} from '../ProjectConfig/ProjectConfig';
import {
  NewProjectCustomField,
  type ProjectCustomField,
  type ProjectCustomFieldWireFormat,
} from '../ProjectCustomField/ProjectCustomField';
import {
  NewProjectPlant,
  type ProjectPlant,
  type ProjectPlantWireFormat,
} from '../ProjectPlant/ProjectPlant';
import {
  NewProjectProduct,
  type ProjectProduct,
  type ProjectProductWireFormat,
} from '../ProjectProduct/ProjectProduct';
import {
  NewProjectStatus,
  ProjectStatus,
  type ProjectStatusWireFormat,
} from '../ProjectStatus/ProjectStatus';
import { NewSegment, type Segment, type SegmentWireFormat } from '../Segment/Segment';
import { NewTaxCode, type TaxCode, type TaxCodeWireFormat } from '../TaxCode/TaxCode';
import { NewUser, User, type UserWireFormat } from '../User/User';

export class Project {
  readonly id: string;
  readonly name: string;
  readonly projectNumber: string | null;
  readonly projectJobNumber: string | null;
  readonly address: Address;
  readonly isPubliclyFunded: boolean | null;
  readonly winningCompany: ProjectCompany | null;
  readonly companies: ProjectCompany[];
  readonly projectStatus: ProjectStatus;
  readonly totalCostsOverride: Currency | null;
  readonly readyMixQuantityOverride: number | null;
  readonly readyMixQuantityOverrideUnit: Enums.Unit | null;
  readonly aggregateQuantityOverride: number | null;
  readonly aggregateQuantityOverrideUnit: Enums.Unit | null;
  readonly asphaltQuantityOverride: number | null;
  readonly asphaltQuantityOverrideUnit: Enums.Unit | null;
  readonly revenueOverride: Currency | null;
  readonly products: ProjectProduct[];
  readonly customFields: ProjectCustomField[];
  readonly plant: Plant;
  readonly plants: ProjectPlant[];
  readonly plantDistance: string | null;
  readonly plantDistanceUnit: Enums.Unit | null;
  readonly plantDriveTime: Duration | null;
  readonly estimatedStartDate: DateTime | null;
  readonly estimatedEndDate: DateTime | null;
  readonly expirationDate: DateTime | null;
  readonly bidDate: DateTime | null;
  readonly confidence: string | null;
  readonly terms: string | null;
  readonly notes: string | null;
  readonly owner: string | null;
  readonly competitor: ProjectCompetitor | null;
  readonly taxCode: TaxCode | null;
  readonly segment: Segment | null;
  readonly user: User;
  readonly projectConfig: ProjectConfig | null;
  readonly forecasts: Forecast[];
  readonly totalCosts: Currency;
  readonly revenue: Currency;
  readonly estimatedVolume: number;
  readonly readyMixQuantity: string;
  readonly readyMixQuantityUnit: Enums.Unit;
  readonly aggregateQuantity: string;
  readonly aggregateQuantityUnit: Enums.Unit;
  readonly asphaltQuantity: string;
  readonly asphaltQuantityUnit: Enums.Unit;
  readonly createdAt: DateTime;
  readonly createdBy: User | null;
  readonly lastStatusChange: DateTime | null;
  readonly lastStatusChangeBy: User | null;

  constructor(data: { [key: string]: any } = {}) {
    this.id = data.id === undefined ? NIL_UUID : data.id;
    this.name = data.name === undefined ? '' : data.name;
    this.projectNumber = data.projectNumber === undefined ? null : data.projectNumber;
    this.projectJobNumber = data.projectJobNumber === undefined ? null : data.projectJobNumber;
    this.address = NewAddress(data.address);
    this.isPubliclyFunded = data.isPubliclyFunded === undefined ? null : data.isPubliclyFunded;
    this.winningCompany =
      // eslint-disable-next-line no-nested-ternary
      (data.winningCompany ?? null) === null ? null : NewProjectCompany(data.winningCompany);
    this.companies = (data.companies ?? []).map((companies: any) => NewProjectCompany(companies));
    this.projectStatus = NewProjectStatus(data.projectStatus);
    this.totalCostsOverride =
      (data.totalCostsOverride ?? null) === null ? null : NewCurrency(data.totalCostsOverride);
    this.readyMixQuantityOverride =
      data.readyMixQuantityOverride === undefined ? null : data.readyMixQuantityOverride;
    this.readyMixQuantityOverrideUnit =
      data.readyMixQuantityOverrideUnit === undefined ? null : data.readyMixQuantityOverrideUnit;
    this.aggregateQuantityOverride =
      data.aggregateQuantityOverride === undefined ? null : data.aggregateQuantityOverride;
    this.aggregateQuantityOverrideUnit =
      data.aggregateQuantityOverrideUnit === undefined ? null : data.aggregateQuantityOverrideUnit;
    this.asphaltQuantityOverride =
      data.asphaltQuantityOverride === undefined ? null : data.asphaltQuantityOverride;
    this.asphaltQuantityOverrideUnit =
      data.asphaltQuantityOverrideUnit === undefined ? null : data.asphaltQuantityOverrideUnit;
    this.revenueOverride =
      (data.revenueOverride ?? null) === null ? null : NewCurrency(data.revenueOverride);
    this.products = (data.products ?? []).map((products: any) => NewProjectProduct(products));
    this.customFields = (data.customFields ?? []).map((customFields: any) =>
      NewProjectCustomField(customFields),
    );
    this.plant = NewPlant(data.plant);
    this.plants = (data.plants ?? []).map((plants: any) => NewProjectPlant(plants));
    this.plantDistance = data.plantDistance === undefined ? null : data.plantDistance;
    this.plantDistanceUnit = data.plantDistanceUnit === undefined ? null : data.plantDistanceUnit;
    this.plantDriveTime = DateHelpers.ParseServerNullableDuration(data.plantDriveTime);
    this.estimatedStartDate = DateHelpers.ParseServerNullableDate(data.estimatedStartDate);
    this.estimatedEndDate = DateHelpers.ParseServerNullableDate(data.estimatedEndDate);
    this.expirationDate = DateHelpers.ParseServerNullableDate(data.expirationDate);
    this.bidDate = DateHelpers.ParseServerNullableDate(data.bidDate);
    this.confidence = data.confidence === undefined ? null : data.confidence;
    this.terms = data.terms === undefined ? null : data.terms;
    this.notes = data.notes === undefined ? null : data.notes;
    this.owner = data.owner === undefined ? null : data.owner;
    this.competitor =
      // eslint-disable-next-line no-nested-ternary
      (data.competitor ?? null) === null ? null : NewProjectCompetitor(data.competitor);
    this.taxCode =
      // eslint-disable-next-line no-nested-ternary
      (data.taxCode ?? null) === null ? null : NewTaxCode(data.taxCode);
    this.segment =
      // eslint-disable-next-line no-nested-ternary
      (data.segment ?? null) === null ? null : NewSegment(data.segment);
    this.user = NewUser(data.user);
    this.projectConfig =
      // eslint-disable-next-line no-nested-ternary
      (data.projectConfig ?? null) === null ? null : NewProjectConfig(data.projectConfig);
    this.forecasts = (data.forecasts ?? []).map((forecasts: any) => NewForecast(forecasts));
    this.totalCosts = NewCurrency(data.totalCosts);
    this.revenue = NewCurrency(data.revenue);
    this.estimatedVolume = data.estimatedVolume === undefined ? 0 : data.estimatedVolume;
    this.readyMixQuantity = data.readyMixQuantity === undefined ? '0' : data.readyMixQuantity;
    this.readyMixQuantityUnit =
      data.readyMixQuantityUnit === undefined ? Enums.Unit.CuYd : data.readyMixQuantityUnit;
    this.aggregateQuantity = data.aggregateQuantity === undefined ? '0' : data.aggregateQuantity;
    this.aggregateQuantityUnit =
      data.aggregateQuantityUnit === undefined ? Enums.Unit.Tn : data.aggregateQuantityUnit;
    this.asphaltQuantity = data.asphaltQuantity === undefined ? '0' : data.asphaltQuantity;
    this.asphaltQuantityUnit =
      data.asphaltQuantityUnit === undefined ? Enums.Unit.Tn : data.asphaltQuantityUnit;
    this.createdAt = DateHelpers.ParseServerDate(data.createdAt);
    this.createdBy =
      // eslint-disable-next-line no-nested-ternary
      (data.createdBy ?? null) === null ? null : NewUser(data.createdBy);
    this.lastStatusChange = DateHelpers.ParseServerNullableDate(data.lastStatusChange);
    this.lastStatusChangeBy =
      // eslint-disable-next-line no-nested-ternary
      (data.lastStatusChangeBy ?? null) === null ? null : NewUser(data.lastStatusChangeBy);
  }

  static zero(): Project {
    const zeroValues: ExcludeMethodsDeep<Project> = {
      id: NIL_UUID,
      name: '',
      projectNumber: null,
      projectJobNumber: null,
      address: Address.zero(),
      isPubliclyFunded: null,
      winningCompany: null,
      companies: [],
      projectStatus: ProjectStatus.zero(),
      totalCostsOverride: null,
      readyMixQuantityOverride: null,
      readyMixQuantityOverrideUnit: null,
      aggregateQuantityOverride: null,
      aggregateQuantityOverrideUnit: null,
      asphaltQuantityOverride: null,
      asphaltQuantityOverrideUnit: null,
      revenueOverride: null,
      products: [],
      customFields: [],
      plant: Plant.zero(),
      plants: [],
      plantDistance: null,
      plantDistanceUnit: null,
      plantDriveTime: null,
      estimatedStartDate: null,
      estimatedEndDate: null,
      expirationDate: null,
      bidDate: null,
      confidence: null,
      terms: null,
      notes: null,
      owner: null,
      competitor: null,
      taxCode: null,
      segment: null,
      user: User.zero(),
      projectConfig: null,
      forecasts: [],
      totalCosts: Currency.zero(),
      revenue: Currency.zero(),
      estimatedVolume: 0,
      readyMixQuantity: '0',
      readyMixQuantityUnit: Enums.Unit.CuYd,
      aggregateQuantity: '0',
      aggregateQuantityUnit: Enums.Unit.Tn,
      asphaltQuantity: '0',
      asphaltQuantityUnit: Enums.Unit.Tn,
      createdAt: DateHelpers.ParseServerDate(DateHelpers.ZERO_DATE_STRING),
      createdBy: null,
      lastStatusChange: null,
      lastStatusChangeBy: null,
    };
    return new Project(zeroValues);
  }

  // ************* DO NOT EDIT ABOVE THIS LINE *************

  /** Given a ProjectList and a ForecastList, return a copy of the ProjectList that is filled with those forecasts. */
  static fillForecasts(
    projectList: List<Project>,
    forecastList: List<Forecast>,
    tenantUnits: Units,
  ): List<Project> {
    const newProject = (props: PartialDeep<Project, { recurseIntoArrays: true }>): Project =>
      new Project(props);

    const filledProjects = projectList.items.map((p) => {
      const matchingProjectForecasts = forecastList.items.filter((f) => f.projectId === p.id);
      return newProject({
        ...p,
        forecasts: Forecast.fillForecasts({
          currentForecasts: matchingProjectForecasts,
          kind: Enums.ForecastKind.Project,
          productCategory: Enums.ProductCategory.ReadyMix, // Default to Mix for backward compatibility
          tenantUnits,
        }),
      });
    });
    const projectListData: ExcludeMethodsDeep<List<Project>> = {
      items: filledProjects,
      count: filledProjects.length,
    };
    return new List(newProject, projectListData);
  }

  fillForecasts(forecasts: List<Forecast> | Forecast[], tenantUnits: Units): Project {
    const forecastList =
      forecasts instanceof List
        ? forecasts
        : new List(NewForecast, { items: forecasts, count: forecasts.length });
    return Project.fillForecasts(
      new List((props: PartialDeep<Project, { recurseIntoArrays: true }>) => new Project(props), {
        items: [this],
        count: 1,
      }),
      forecastList,
      tenantUnits,
    ).items[0];
  }

  winningCompanyName(): string | null {
    if (this.winningCompany === null) {
      return null;
    }

    if (this.winningCompany.contact === null) {
      return this.winningCompany.company.name;
    }

    return `${this.winningCompany.company.name} - ${this.winningCompany.contact.fullName()}`;
  }

  /**
   * Mirrors ProjectSummary.contractorDisplay as rendered in SQL:
   * winning company name when set (falling back to "0 bidders" if there are
   * no Contractor companies), otherwise the sole contractor name or "N bidders".
   */
  contractorDisplay(): string {
    const contractors = this.companies.filter(
      (pc) => pc.company.category === Enums.CompanyCategory.Contractor,
    );

    if (this.winningCompany !== null) {
      const winningIsContractor =
        this.winningCompany.company.category === Enums.CompanyCategory.Contractor;
      if (!winningIsContractor && contractors.length === 0) {
        return '0 bidders';
      }
      return this.winningCompany.company.name;
    }

    if (contractors.length === 0) {
      return '0 bidders';
    }
    if (contractors.length === 1) {
      return contractors[0].company.name;
    }
    return `${contractors.length} bidders`;
  }

  confidenceDisplay(): string | null {
    if (this.confidence === null) {
      return null;
    }
    // TODO: Replace this with decimal precision in sc-1251
    return `${String(parseFloat((parseFloat(this.confidence) * 100).toFixed(3)))}%`;
  }

  estimatedStartDateDisplay(locale: string): string | null {
    if (this.estimatedStartDate === null) {
      return null;
    }
    return DateHelpers.FormatDisplayDateTime(this.estimatedStartDate, { locale });
  }

  estimatedEndDateDisplay(locale: string): string | null {
    if (this.estimatedEndDate === null) {
      return null;
    }
    return DateHelpers.FormatDisplayDateTime(this.estimatedEndDate, { locale });
  }

  bidDateDisplay(locale: string): string | null {
    if (this.bidDate === null) {
      return null;
    }
    return DateHelpers.FormatDisplayDateTime(this.bidDate, { locale });
  }

  expirationDateDisplay(locale: string): string | null {
    if (this.expirationDate === null) {
      return null;
    }
    return DateHelpers.FormatDisplayDateTime(this.expirationDate, { locale });
  }

  estimatedMargin(): number {
    const revenue = this.revenue.toUnit();
    const totalCosts = this.totalCosts.toUnit();
    return revenue - totalCosts;
  }

  /** (margin / revenue) * 100 */
  estimatedMarginPercent(opts?: {
    /** @default true */
    includePercent?: boolean;
    /** @default 2 */
    fractionDigits?: number;
  }): string {
    const { includePercent, fractionDigits } = {
      includePercent: opts?.includePercent ?? true,
      fractionDigits: opts?.fractionDigits ?? 2,
    };

    const revenue = this.revenue.toUnit();

    const estimatedMarginNumber = revenue === 0 ? 0 : (this.estimatedMargin() / revenue) * 100;

    const marginString = estimatedMarginNumber.toLocaleString('en-US', {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });
    return `${marginString}${includePercent ? '%' : ''}`;
  }

  /** revenue / volume */
  pricePerReadyMixQuantityUnitDisplay(): string {
    const revenue = this.revenue.toUnit();
    const readyMixQuantity = Number(this.readyMixQuantityOverride ?? this.readyMixQuantity);
    if (revenue === 0 || readyMixQuantity === 0) {
      return CurrencyHelpers.CurrencyString({
        cur: Currency.zero(),
        currencyCode: this.plant.currencyCode,
      });
    }
    const pricePerYardData: ExcludeMethodsDeep<Currency> = {
      number: String(revenue / readyMixQuantity),
      currency: 'USD',
    };
    return CurrencyHelpers.CurrencyString({
      cur: new Currency(pricePerYardData),
      currencyCode: this.plant.currencyCode,
    });
  }

  estimatedMarginDisplay(): string {
    const marginData: ExcludeMethodsDeep<Currency> = {
      number: String(this.estimatedMargin()),
      currency: 'USD',
    };
    return CurrencyHelpers.CurrencyString({
      cur: new Currency(marginData),
      currencyCode: this.plant.currencyCode,
    });
  }
  // ************* DO NOT EDIT BELOW THIS LINE *************
}

export type ProjectWireFormat = {
  id: string;
  name: string;
  projectNumber: string | null;
  projectJobNumber: string | null;
  address: AddressWireFormat;
  isPubliclyFunded: boolean | null;
  winningCompany: ProjectCompanyWireFormat | null;
  companies: ProjectCompanyWireFormat[];
  projectStatus: ProjectStatusWireFormat;
  totalCostsOverride: CurrencyWireFormat | null;
  readyMixQuantityOverride: number | null;
  readyMixQuantityOverrideUnit: string | null;
  aggregateQuantityOverride: number | null;
  aggregateQuantityOverrideUnit: string | null;
  asphaltQuantityOverride: number | null;
  asphaltQuantityOverrideUnit: string | null;
  revenueOverride: CurrencyWireFormat | null;
  products: ProjectProductWireFormat[];
  customFields: ProjectCustomFieldWireFormat[];
  plant: PlantWireFormat;
  plants: ProjectPlantWireFormat[];
  plantDistance: string | null;
  plantDistanceUnit: string | null;
  plantDriveTime: string | null;
  estimatedStartDate: string | null;
  estimatedEndDate: string | null;
  expirationDate: string | null;
  bidDate: string | null;
  confidence: string | null;
  terms: string | null;
  notes: string | null;
  owner: string | null;
  competitor: ProjectCompetitorWireFormat | null;
  taxCode: TaxCodeWireFormat | null;
  segment: SegmentWireFormat | null;
  user: UserWireFormat;
  projectConfig: ProjectConfigWireFormat | null;
  forecasts: ForecastWireFormat[];
  totalCosts: CurrencyWireFormat;
  revenue: CurrencyWireFormat;
  estimatedVolume: number;
  readyMixQuantity: string;
  readyMixQuantityUnit: string;
  aggregateQuantity: string;
  aggregateQuantityUnit: string;
  asphaltQuantity: string;
  asphaltQuantityUnit: string;
  createdAt: string;
  createdBy: UserWireFormat | null;
  lastStatusChange: string | null;
  lastStatusChangeBy: UserWireFormat | null;
};

type NewProjectProps = PartialDeep<Project, { recurseIntoArrays: true }>;
export const NewProject: (props: NewProjectProps) => Project = (props) => new Project(props);

type NewProjectFromWireFormatProps = PartialDeep<ProjectWireFormat, { recurseIntoArrays: true }>;
export const NewProjectFromWireFormat: (props: NewProjectFromWireFormatProps) => Project = (
  props,
) => new Project(props);
